import { useMemo, useState } from 'react'

import { isObject } from 'lodash'
import { useSelector } from 'react-redux'
import { Input } from 'antd'
import { faCalendar, faClinicMedical, faGraduationCap, faCalendarStar, faHospital, faPenField, faStethoscope } from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import FormItem from '../../../Components/shared/Forms/FormItem'
import { getEntityOrNull, getIdOrNull } from '../../../utils'
import { InternshipRequest, InternshipRequestProperties as RequestProperties } from '../../../utils/entities/internshipRequest'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import MultiPeriodsPicker from '../../../Components/antd/Pickers/MultiPeriodsPicker'
import HospitalDepartmentsSelector from '../../../Components/shared/Selectors/HospitalDepartments/HospitalDepartmentsSelector'
import SectorSelector from '../../../Components/shared/Selectors/SectorSelector'
import InternshipRequestFilesForm from './InternshipRequestFilesForm'
import SectionsSelector from '../../../Components/shared/Selectors/SectionsSelector'
import SectionToYearSelector from '../../../Components/shared/Selectors/SectionToYearSelector'

const FIELD_WIDTH = '100%'
const FIELD_STYLE = { width: FIELD_WIDTH }

const { INSTITUTION, REQUEST_PERIODS, SECTOR, SECTION_TO_YEAR, COMMENT } = RequestProperties

const InternshipRequestForm = ({
  errorFields = [], data, files, requiredFields = {}, displayTitle, hospitalDepartments, institution, institutionGroup, sections, sectionToYears,
  onChange, setFiles
}) => {
  const t = useSelector(selectTranslate)

  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const [selectedSection, setSelectedSection] = useState(null)

  const handleSelect = key => (value, _, entity) => onChange(new InternshipRequest({ ...data, [key]: getEntityOrNull(entity) ?? getEntityOrNull(value) }))
  const handleChange = key => value => onChange(new InternshipRequest({ ...data, [key]: value }))
  const handleEventChange = key => e => onChange(new InternshipRequest({ ...data, [key]: e.target.value }))

  const handleSectionChange = value => {
    setSelectedSection(value)
    handleChange(SECTION_TO_YEAR)(null)
  }

  const handleDepartmentChange = value => {
    onChange(new InternshipRequest({ ...data, [SECTOR]: null }))
    setSelectedDepartment(hospitalDepartments.find(d => d.id === value))
  }

  const handleInstitutionChange = (value, _, entity) => {
    if (getIdOrNull(institutionGroup)) {
      handleSelect(INSTITUTION)(value, _, entity)
      handleDepartmentChange(null)
    }
  }

  const selectedInstitution = institution

  const availableSectors = useMemo(() => {
    let sectors = []

    if (getIdOrNull(selectedInstitution)) {
      sectors = getIdOrNull(selectedDepartment) ? selectedDepartment?.sectors : selectedInstitution?.sectors
    }

    return sectors
  }, [selectedDepartment?.sectors, selectedInstitution?.sectors])

  const isInstitutionSelectorDisabled = getIdOrNull(institution) !== null
  const className = field => errorFields.includes(field) ? 'error-ant-border' : ''

  return (
    <>
      <div className='pd-12'>
        {displayTitle && <h3> {t('common.internship')}</h3>}

        <div className='flex-row w-100'>
          <FormItem className={'w-50 pdr-4 ' + className(SECTION_TO_YEAR)} icon={faGraduationCap} label='form.label.section' required>
            <SectionsSelector
              className={className(SECTION_TO_YEAR)}
              multiple='default'
              sections={sections}
              style={FIELD_STYLE}
              values={selectedSection}
              onChange={handleSectionChange}
            />
          </FormItem>
          <FormItem className={'w-50 pdl-4 ' + className(SECTION_TO_YEAR)} icon={faCalendarStar} label='form.label.year' required>
            <SectionToYearSelector
              className={className(SECTION_TO_YEAR)}
              multiple='default'
              section={selectedSection}
              sectionToYears={sectionToYears}
              style={FIELD_STYLE}
              values={getIdOrNull(selectedSection) ? getIdOrNull(data[SECTION_TO_YEAR]) : null}
              onChange={handleSelect(SECTION_TO_YEAR)}
            />
          </FormItem>
        </div>

        <FormItem icon={faCalendar} label='form.label.periods' required>
          <MultiPeriodsPicker
            className={className(REQUEST_PERIODS)}
            periods={data[REQUEST_PERIODS]}
            onChange={handleChange(REQUEST_PERIODS)}
          />
        </FormItem>

        <FormItem icon={faHospital} label='common.institution.dd' className='form-select' required>
          {isObject(institutionGroup) ? (
            <InstitutionsSelector
              disabled={isInstitutionSelectorDisabled}
              className={className(INSTITUTION)}
              values={getIdOrNull(data[INSTITUTION])}
              onChange={handleInstitutionChange}
              institutions={institutionGroup.institutions}
              multiple={false}
            />
          ) : (
            <Input value={institution.name} disabled={isInstitutionSelectorDisabled} />
          )}
        </FormItem>

        <FormItem icon={faClinicMedical} label='common.hospital_department.dd' className='form-select'>
          <HospitalDepartmentsSelector
            className='w-100'
            multiple='default'
            disabled={getIdOrNull(selectedInstitution) === null}
            hospitalDepartments={hospitalDepartments}
            values={getIdOrNull(selectedDepartment)}
            onChange={handleDepartmentChange}
          />
        </FormItem>

        <FormItem icon={faStethoscope} label='common.sector.dd' className='form-select'>
          <SectorSelector
            disabled={getIdOrNull(selectedInstitution) === null}
            sectors={availableSectors}
            values={getIdOrNull(data[SECTOR])}
            onChange={handleChange(SECTOR)}
          />
        </FormItem>

        <InternshipRequestFilesForm
          errorFields={errorFields}
          files={files}
          setFiles={setFiles}
          requiredFields={requiredFields}
        />

        <FormItem icon={faPenField} label='form.label.comment'>
          <Input.TextArea
            maxLength={500}
            rows={4}
            value={data[COMMENT]}
            onChange={handleEventChange(COMMENT)}
          />
        </FormItem>
      </div>
    </>
  )
}

export default InternshipRequestForm
